import { ReactNode } from 'react'
import { ISearchLocation, MkApiContext, MkSavedSearch, SaveSearchPayload } from '@typescript'
import { MkFilterCategory, MkSearchResponse, MkRoomType, SavedSearchFiltersPayload } from './services/searchTypes'
import { AvailabilityContextProps } from './context'
import type { SearchConstraints } from './utils/searchConstraintsUtils'

export interface AvailabilityState {
  activeUnitName: UnitType
  suites: TMkAvailableSuite[]
  selectedSuite: string | null
  selectedResort: string | null
  continuousDays: number
  minimumNights: number
  openToDate: Date | null | undefined
  purchaseType: string
  isRatePlanLoaded: boolean
  fetchingDates: boolean
  isBonusTimeApplicableForResort: boolean
  isBonusTimeEnabled: boolean
  isExoticBonusTimeApplicableForResort: boolean
  isExoticBonusTimeEnabled: boolean
  isLoading: boolean
  preLoadAvailabiliySearch: boolean
  currentMonth: Date | null | undefined
  startDate: Date | null | undefined
  checkInDate: string | null
  endDate: Date | null | undefined
  checkOutDate: string | null
  includedDays: Date[]
  availabilityError: Error | undefined
  location: ISearchLocation | undefined
  isFlexibleDates: boolean
  isBonusTimeDates: boolean
  numberOfNights: number
  monthYears: string
  flexiDatesString: string
  bonusTimeDatesString: string
  isDatesBufferEnabled: boolean
  selectedFlexibleDateMonths: Date[]
  /** From GET /search/searchConstraints when logged in. Drives flexi + specific date limits. */
  searchConstraints: SearchConstraints | null
  savedSearch: string | SaveSearchPayload | undefined
  savedSearches: MkSavedSearch[]
  savedSearchesError: Error | undefined
  saveSearchSuccess: boolean
  saveSearchError: Error | undefined
  deleteSearchSuccess: boolean
  deleteSearchError: Error | undefined
  filters: any | undefined
  unfilteredSearchResults: MkSearchResponse | undefined
  searchAlertMessage: string
  searchBusinessSystemCode: string
  setIsSavedSearchExecution: (isSavedSearchExecution: boolean) => void
  savedSearchFiltersPayload: SavedSearchFiltersPayload[] | undefined
  isSavedSearchExecution: boolean | undefined
}

export enum AvailabilityActionType {
  CHECKOUT_AVAIL_FETCH_SUCCESS = 'CHECKOUT_AVAIL_FETCH_SUCCESS',
  CHECKOUT_AVAIL_FETCH_FAILURE = 'CHECKOUT_AVAIL_FETCH_FAILURE',
  CHECKIN_RATEPLAN_FETCH_SUCCESS = 'CHECKIN_RATEPLAN_FETCH_SUCCESS',
  CHECKIN_RATEPLAN_FETCH_FAILURE = 'CHECKIN_RATEPLAN_FETCH_FAILURE',
  SET_BONUS_TIME_STATE = 'SET_BONUS_TIME_STATE',
  SET_CURRENT_MONTH = 'SET_CURRENT_MONTH',
  SET_END_DATE = 'SET_END_DATE',
  SET_LOCATION = 'SET_LOCATION',
  SET_START_DATE = 'SET_START_DATE',
  RESET_DATES = 'RESET_DATES',
  RESET_LOCATION = 'RESET_LOCATION',
  RESET_FLEXIBLE_DATES = 'RESET_FLEXIBLE_DATES',
  RESET_BONUS_TIME_DATES = 'RESET_BONUS_TIME_DATES',
  SET_FLEXIBLE_DATES = 'SET_FLEXIBLE_DATES',
  SET_BONUS_TIME_DATES = 'SET_BONUS_TIME_DATES',
  SET_BONUS_TIME_DATES_STRING = 'SET_BONUS_TIME_DATES_STRING',
  SET_DATES_BUFFER_ENABLED = 'SET_DATES_BUFFER_ENABLED',
  SET_SELECTED_FLEXIBLE_DATE_MONTHS = 'SET_SELECTED_FLEXIBLE_DATE_MONTHS',
  SET_NUMBER_OF_NIGHTS = 'SET_NUMBER_OF_NIGHTS',
  SET_MONTH_YEARS = 'SET_MONTH_YEARS',
  SET_FLEXI_DATES_STRING = 'SET_FLEXI_DATES_STRING',
  SET_AVAILABLE_SUITES = 'SET_AVAILABLE_SUITES',
  SET_SELECTED_SUITE = 'SET_SELECTED_SUITE',
  SET_SELECTED_RESORT = 'SET_SELECTED_RESORT',
  SET_SAVED_SEARCH = 'SET_SAVED_SEARCH',
  SET_LOADING_STATE = 'SET_LOADING_STATE',
  FETCH_SAVED_SEARCHES_SUCCESS = 'FETCH_SAVED_SEARCHES_SUCCESS',
  FETCH_SAVED_SEARCHES_FAILURE = 'FETCH_SAVED_SEARCHES_FAILURE',
  SAVE_SEARCH_SUCCESS = 'SAVE_SEARCH_SUCCESS',
  SAVE_SEARCH_FAILURE = 'SAVE_SEARCH_FAILURE',
  DELETE_SAVED_SEARCH_SUCCESS = 'DELETE_SAVED_SEARCH_SUCCESS',
  DELETE_SAVED_SEARCH_FAILURE = 'DELETE_SAVED_SEARCH_FAILURE',
  SET_FILTERS = 'SET_FILTERS',
  UPDATE_FILTERS_FROM_RESPONSE = 'UPDATE_FILTERS_FROM_RESPONSE',
  SET_UNFILTERED_SEARCH_RESULTS = 'SET_UNFILTERED_SEARCH_RESULTS',
  SET_SAVED_SEARCH_FILTERS_PAYLOAD = 'SET_SAVED_SEARCH_FILTERS_PAYLOAD',
  SET_IS_SAVED_SEARCH_EXECUTION = 'SET_IS_SAVED_SEARCH_EXECUTION',
  SET_SEARCH_CONSTRAINTS = 'SET_SEARCH_CONSTRAINTS',
  SET_SEARCH_RESPONSE_META = 'SET_SEARCH_RESPONSE_META',
}

export type AvailabilityAction =
  | { type: AvailabilityActionType.SET_START_DATE; payload: Date | null }
  | { type: AvailabilityActionType.SET_END_DATE; payload: Date | null }
  | { type: AvailabilityActionType.SET_CURRENT_MONTH; payload: Date | null }
  | { type: AvailabilityActionType.RESET_DATES }
  | { type: AvailabilityActionType.RESET_LOCATION }
  | { type: AvailabilityActionType.RESET_FLEXIBLE_DATES }
  // The payload will be properly type once we have the API response.
  | { type: AvailabilityActionType.CHECKIN_RATEPLAN_FETCH_SUCCESS; payload: any }
  | { type: AvailabilityActionType.CHECKIN_RATEPLAN_FETCH_FAILURE; payload: Error }
  // The payload will be properly type once we have the API response.
  | { type: AvailabilityActionType.CHECKOUT_AVAIL_FETCH_SUCCESS; payload: any }
  | { type: AvailabilityActionType.CHECKOUT_AVAIL_FETCH_FAILURE; payload: Error }
  | {
      type: AvailabilityActionType.SET_BONUS_TIME_STATE
      payload: {
        isBonusTimeEnabled: boolean
        isExoticBonusTimeEnabled: boolean
      }
    }
  | { type: AvailabilityActionType.SET_LOCATION; payload: ISearchLocation }
  | { type: AvailabilityActionType.SET_FLEXIBLE_DATES; payload: boolean }
  | { type: AvailabilityActionType.SET_BONUS_TIME_DATES; payload: boolean }
  | { type: AvailabilityActionType.RESET_BONUS_TIME_DATES }
  | { type: AvailabilityActionType.SET_DATES_BUFFER_ENABLED; payload: boolean }
  | { type: AvailabilityActionType.SET_SELECTED_FLEXIBLE_DATE_MONTHS; payload: Date[] }
  | { type: AvailabilityActionType.SET_NUMBER_OF_NIGHTS; payload: number }
  | { type: AvailabilityActionType.SET_MONTH_YEARS; payload: string }
  | { type: AvailabilityActionType.SET_FLEXI_DATES_STRING; payload: string }
  | { type: AvailabilityActionType.SET_BONUS_TIME_DATES_STRING; payload: string }
  | { type: AvailabilityActionType.SET_AVAILABLE_SUITES; payload: TMkAvailableSuite[] }
  | { type: AvailabilityActionType.SET_SELECTED_SUITE; payload: string | null }
  | { type: AvailabilityActionType.SET_SELECTED_RESORT; payload: string | null }
  | { type: AvailabilityActionType.SET_SAVED_SEARCH; payload: SaveSearchPayload }
  | { type: AvailabilityActionType.SET_LOADING_STATE; payload: boolean }
  | { type: AvailabilityActionType.FETCH_SAVED_SEARCHES_SUCCESS; payload: MkSavedSearch[] }
  | { type: AvailabilityActionType.FETCH_SAVED_SEARCHES_FAILURE; payload: Error }
  | { type: AvailabilityActionType.SAVE_SEARCH_SUCCESS; payload: SaveSearchPayload }
  | { type: AvailabilityActionType.SAVE_SEARCH_FAILURE; payload: Error }
  | { type: AvailabilityActionType.DELETE_SAVED_SEARCH_SUCCESS; payload: string } // searchId
  | { type: AvailabilityActionType.DELETE_SAVED_SEARCH_FAILURE; payload: Error }
  | { type: AvailabilityActionType.SET_FILTERS; payload: any }
  | { type: AvailabilityActionType.UPDATE_FILTERS_FROM_RESPONSE; payload: any }
  | { type: AvailabilityActionType.SET_UNFILTERED_SEARCH_RESULTS; payload: MkSearchResponse }
  | { type: AvailabilityActionType.SET_SAVED_SEARCH_FILTERS_PAYLOAD; payload: SavedSearchFiltersPayload[] }
  | { type: AvailabilityActionType.SET_IS_SAVED_SEARCH_EXECUTION; payload: boolean }
  | { type: AvailabilityActionType.SET_SEARCH_CONSTRAINTS; payload: SearchConstraints | null }
  | { type: AvailabilityActionType.SET_SEARCH_RESPONSE_META; payload: { alertMessage: string; businessSystemCode: string } }

export interface AvailabilityProviderProps {
  children: ReactNode
  caller?: string
}

export enum UnitType {
  AllSuites = 'All Suites',
  Studio = 'Studio',
  OneBedroom = '1 Bedroom',
  TwoBedroom = '2 Bedroom',
  ThreeBedroom = '3 Bedroom',
}

export type MkAvailabilityResponse = any
export type MkAvailabilityRequest = any
export type MkMemberRatePlanResponse = any
export type MkMemberRatePlanRequest = any
export type MkPreLoginResultsResponse = any
export type MkPreLoginResultsRequest = any
export type MkUserData = {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  address: string
  city: string
  state: string
  zip: string
  country: string
  [key: string]: any
}

export interface AvailabilityServiceConfig {
  context: MkApiContext
  userInfo: MkUserData
}

export abstract class IAvailabilityService {
  static initialState?(): AvailabilityState
  abstract getContextProps(): AvailabilityContextProps
  abstract onLocationChange(location: ISearchLocation): void
  abstract registerBonusTimeState(isBonusTimeEnabled: boolean, isExoticBonusTimeEnabled: boolean): void
  abstract onDateChange(dateSelection: Date | (Date | null)[], currStart?: Date | null, currEnd?: Date | null): void
  abstract onMonthChange(date: Date | (Date | null)[]): void
  abstract resetDates(): void
  abstract onFlexibleDatesChange(isFlexiDates: boolean): void
  abstract onBonusTimeDatesChange(isBonusTimeDates: boolean): void
  abstract setNumberOfNights(numberOfNights: number): void
  abstract setFlexiDatesString(flexiDatesString: string): void
  abstract setBonusTimeDatesString(bonusTimeDatesString: string): void
  abstract setAvailableSuites(suites: TMkAvailableSuite[]): void
  abstract setSelectedSuite(suiteId: string | null): void
  abstract setSelectedResort(resortKey: string | null): void
  abstract setSavedSearch(savedSearch: SaveSearchPayload): void
  abstract setLoadingState(isLoading: boolean): void
  abstract fetchSavedSearches(userName: string, brand: string, accessCode: string, jwtToken: string): Promise<MkSavedSearch[]>
  abstract createSaveSearchPayload(searchName?: string): SaveSearchPayload
  abstract submitSavedSearch(userName: string, brand: string, accessCode: string, jwtToken: string, payload?: SaveSearchPayload): Promise<any>
  abstract searchResorts(options?: {
    pageNum?: number
    pageSize?: number
    filterList?: MkFilterCategory[]
    sortBy?: string
    latitude?: string
    longitude?: string
  }): Promise<MkSearchResponse>
}

export type TMkAvailableSuite = MkRoomType

export interface LocationSearchResult {
  name: string
  type: 'state' | 'resort' | 'region' | 'city'
  subtext: string
  // Optional fields for regions
  id?: string
  imageUrl?: string
  featured?: boolean
  resortCount?: number
  resourceKey?: string
}
